import { siteConfig } from "@/lib/site-config";
// Imported rather than referenced by path so Vite fingerprints the filenames —
// the CDN only serves a long cache lifetime for content-hashed assets.
import logo96 from "@/assets/brand/logo-96.webp";
import logo192 from "@/assets/brand/logo-192.webp";
import logo288 from "@/assets/brand/logo-288.webp";
import logoPng from "@/assets/brand/logo-288.png";

interface LogoProps {
  /** Tailwind height class, e.g. "h-12 md:h-14". Width stays auto. */
  className?: string;
  /** Intrinsic height in CSS pixels, used to reserve layout space. */
  displayHeight: number;
  /** `eager` for the header logo, which is above the fold on every page. */
  loading?: "eager" | "lazy";
}

/** Native aspect ratio of the brand mark (1100 x 807). */
const ASPECT = 1100 / 807;

const WEBP_SRCSET = `${logo96} 96w, ${logo192} 192w, ${logo288} 288w`;

/**
 * The brand mark, served at display resolution instead of full size.
 *
 * The source artwork is 1100x807 but never renders taller than ~64px, so the
 * navbar and footer pull small WebP variants (with a PNG fallback) rather than
 * the full-resolution file. Explicit dimensions keep the header from reflowing
 * while the image loads.
 */
const Logo = ({ className = "", displayHeight, loading = "eager" }: LogoProps) => {
  const width = Math.round(displayHeight * ASPECT);
  return (
    <picture>
      <source type="image/webp" srcSet={WEBP_SRCSET} sizes={`${width}px`} />
      <img
        src={logoPng}
        alt={siteConfig.name}
        className={className}
        width={width}
        height={displayHeight}
        loading={loading}
        decoding="async"
      />
    </picture>
  );
};

export default Logo;
